/**
 * CartContext — Primo carrello ufficiale dell'ecosistema TIME Inside.
 *
 * Architettura modulare, estendibile ai futuri prodotti dell'ecosistema
 * (Disciplinati, Le 5 Regole dell'Equilibrio, Maps Mind, Sessione TIME,
 * TIME Immersion, TRASFOPRODUCT, TIME Watch, ecc.) senza modifiche.
 *
 * Persistenza: localStorage (`time-inside:cart`).
 * In futuro può essere sincronizzato lato server senza cambiare l'API pubblica.
 *
 * Ogni item del carrello ha shape stabile:
 *   { slug, title, subtitle, cover, price, format, quantity }
 *
 * Il totale è calcolato lato client come `price * quantity` sommato.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { findProduct } from "../data/products";

const LS_KEY = "time-inside:cart";

const CartContext = createContext(null);

function loadInitial() {
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((it) => it && typeof it.slug === "string" && Number.isFinite(Number(it.price)))
      .map((it) => {
        // Migrazione lazy: re-idrata stripeLookupKey dai dati canonici del prodotto
        // per gli utenti che avevano già oggetti in cart prima dell'introduzione
        // del campo (Sprint Stripe). Additivo, non distruttivo.
        const canonical = findProduct(it.slug);
        return {
          slug: String(it.slug),
          title: String(it.title || ""),
          subtitle: it.subtitle ? String(it.subtitle) : "",
          cover: it.cover ? String(it.cover) : "",
          price: Number(it.price),
          format: it.format ? String(it.format) : "",
          quantity: Math.max(1, Math.floor(Number(it.quantity) || 1)),
          stripeLookupKey:
            it.stripeLookupKey ||
            (canonical && canonical.stripeLookupKey) ||
            undefined,
        };
      });
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadInitial);

  // Persist to localStorage whenever cart changes.
  useEffect(() => {
    try {
      window.localStorage.setItem(LS_KEY, JSON.stringify(items));
    } catch {
      /* ignore quota errors */
    }
  }, [items]);

  const addItem = useCallback((product, quantity = 1) => {
    const qty = Math.max(1, Math.floor(Number(quantity) || 1));
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.slug === product.slug);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        return next;
      }
      return [
        ...prev,
        {
          slug: product.slug,
          title: product.title,
          subtitle: product.subtitle || "",
          cover: product.cover || "",
          price: Number(product.price) || 0,
          format: product.format || "",
          quantity: qty,
          stripeLookupKey: product.stripeLookupKey || undefined,
        },
      ];
    });
  }, []);

  const removeItem = useCallback((slug) => {
    setItems((prev) => prev.filter((it) => it.slug !== slug));
  }, []);

  const updateQuantity = useCallback((slug, quantity) => {
    const qty = Math.max(1, Math.floor(Number(quantity) || 1));
    setItems((prev) =>
      prev.map((it) => (it.slug === slug ? { ...it, quantity: qty } : it))
    );
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totalQuantity = useMemo(
    () => items.reduce((acc, it) => acc + it.quantity, 0),
    [items]
  );

  const subtotal = useMemo(
    () => items.reduce((acc, it) => acc + it.price * it.quantity, 0),
    [items]
  );

  const value = useMemo(
    () => ({
      items,
      totalQuantity,
      subtotal,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
    }),
    [items, totalQuantity, subtotal, addItem, removeItem, updateQuantity, clearCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart deve essere usato all'interno di <CartProvider>.");
  }
  return ctx;
}

/**
 * Utility di formattazione prezzo €.
 * Es. 27 → "27,00 €"; 14.9 → "14,90 €".
 * Additivo: usa toLocaleString it-IT.
 */
export function formatEuro(value) {
  const n = Number(value) || 0;
  return `${n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}
